import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  AgnesPageHeader,
} from '@agnes/ui';
import { Eye, RefreshCw } from 'lucide-react';
import { useCentralReprocessamentoStore } from '@/stores/useCentralReprocessamentoStore';
import { CENTRAL_REPROCESSAMENTO_TIPOS_ENUM } from '@/constants/central-reprocessamento/ConstantCentralReprocessamentoTipos';
import UtilsMask from '@/utils/UtilsMask';
import UtilsDate from '@/utils/UtilsDate';
import { BadgeCentralReprocessamentoStatus } from '@/components/central-reprocessamento/BadgeCentralReprocessamentoStatus';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

type Row = any;

// Histórico de reprocessamento de um empregador (CON-5281): a home mantém só o último
// processamento de cada CNPJ; aqui listamos TODOS os do CNPJ, do mais recente ao mais antigo.
export default function PageCentralReprocessamentoHistorico() {
  const { nrinsc = '' } = useParams();
  const navigate = useNavigate();
  const lista = useCentralReprocessamentoStore((s) => s.listaHistorico);
  const total = useCentralReprocessamentoStore((s) => s.totalHistorico);
  const loading = useCentralReprocessamentoStore((s) => s.estaPesquisandoHistorico);
  const paginacao = useCentralReprocessamentoStore((s) => s.paginacaoHistorico);

  React.useEffect(() => {
    const { paginacaoHistorico } = useCentralReprocessamentoStore.getState();
    useCentralReprocessamentoStore
      .getState()
      .setPaginacaoHistorico({ ...paginacaoHistorico, page: 1 });
    useCentralReprocessamentoStore.getState().pesquisarHistorico(nrinsc);
  }, [nrinsc]);

  const columns = [
    { key: 'id', header: 'ID', width: 56, sortable: true },
    {
      key: 'tipo',
      header: 'Tipo',
      width: 200,
      render: (r: Row) => CENTRAL_REPROCESSAMENTO_TIPOS_ENUM.get(r.tipo).getDescription(),
    },
    {
      key: 'razaoSocial',
      header: 'Razão Social',
      width: 220,
      render: (r: Row) => r.razaoSocial || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'nrinsc',
      header: 'Raíz CNPJ',
      width: 200,
      render: (r: Row) => UtilsMask.cnpj(r.nrinsc),
    },
    {
      key: 'status',
      header: 'Status',
      width: 200,
      render: (r: Row) => <BadgeCentralReprocessamentoStatus status={r.status} />,
    },
    {
      key: 'duracao',
      header: 'Duração',
      width: 150,
      render: (r: Row) => UtilsDate.duration(r.iniciadoEm, r.finalizadoEm || new Date()),
    },
    {
      key: 'atualizadoEm',
      header: 'Última atualização em',
      width: 200,
      sortable: true,
      render: (r: Row) =>
        UtilsDate.format2(r.atualizadoEm, { toFormat: 'DD/MM/YYYY [às] HH:mm:ss' } as any),
    },
  ];

  function onPageChange(zeroBasedPage: number) {
    useCentralReprocessamentoStore
      .getState()
      .setPaginacaoHistorico({ ...paginacao, page: zeroBasedPage + 1 });
    useCentralReprocessamentoStore.getState().pesquisarHistorico(nrinsc);
  }

  function onSortChange(sortBy: string, sortOrder: 'asc' | 'desc') {
    useCentralReprocessamentoStore.getState().setPaginacaoHistorico({
      ...paginacao,
      page: 1,
      sortBy: [{ key: sortBy, order: sortOrder }],
    });
    useCentralReprocessamentoStore.getState().pesquisarHistorico(nrinsc);
  }

  return (
    <>
      <AgnesPageHeader
        icon={<RefreshCw />}
        title="Histórico de reprocessamento"
        subtitle={`Processamentos anteriores do empregador ${UtilsMask.cnpj(nrinsc)}`}
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Administração' },
          { label: 'eSocial' },
          { label: 'Central de Reprocessamento', href: '/central-de-reprocessamento' },
          { label: 'Histórico' },
        ]}
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <TableWrapper>
            <AgnesDataTable<Row>
              data-qa="reprocessamento-historico-tabela"
              columns={columns}
              data={lista}
              rowKey={(r) => String(r.id)}
              actions={(r: Row) => (
                <AgnesButton
                  variant="ghost"
                  size="sm"
                  title="Visualizar"
                  aria-label="Visualizar"
                  data-qa="reprocessamento-historico-acao-visualizar"
                  onClick={() => navigate(`/central-de-reprocessamento/${r.id}`)}
                >
                  <Eye className="h-4 w-4" />
                </AgnesButton>
              )}
              loading={loading}
              sort={{
                sortBy: paginacao.sortBy?.[0]?.key ?? '',
                sortOrder: paginacao.sortBy?.[0]?.order ?? 'desc',
                onSortChange,
              }}
              pagination={{
                page: (paginacao.page ?? 1) - 1,
                pageSize: paginacao.itemsPerPage,
                total,
                onPageChange,
                serverSide: true,
              }}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>
    </>
  );
}
